import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAssessmentStatus } from '../../hooks/useAssessment';
import type { AssessmentSummary } from '../../types';

interface AssessmentHistoryCardProps {
  className?: string;
}

export const AssessmentHistoryCard: React.FC<AssessmentHistoryCardProps> = ({ className = '' }) => {
  const { data: assessmentStatus, isLoading, error } = useAssessmentStatus();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <div className="text-center space-y-4 p-2">
          <div className="w-14 h-14 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-secondary-500/10">
            <svg className="w-7 h-7 text-secondary-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-slate-900 text-lg">Meus Resultados</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Carregando histórico...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <div className="text-center space-y-4 p-2">
          <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-red-500/10">
            <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-slate-900 text-lg">Meus Resultados</h3>
            <p className="text-sm text-red-600 leading-relaxed">
              Erro ao carregar dados
            </p>
          </div>
          <div className="pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full font-medium" 
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Se não há assessment concluído, mostrar estado vazio
  if (!assessmentStatus?.hasCompleted) {
    return (
      <Card className={className}>
        <div className="text-center space-y-4 p-2">
          <div className="w-14 h-14 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-slate-500/10">
            <svg className="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-slate-900 text-lg">Meus Resultados</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Você ainda não possui avaliações concluídas
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Se há assessment concluído, mostrar os dados
  const completedAssessment = assessmentStatus.completedAssessment;
  
  return (
    <Card className={className}>
      <div className="text-center space-y-4 p-2">
        <div className="w-14 h-14 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-secondary-500/10">
          <svg className="w-7 h-7 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="font-semibold text-slate-900 text-lg">Meus Resultados</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            1 avaliação concluída
          </p>
        </div>
        
        {/* Latest Assessment Preview */}
        {completedAssessment && (
          <div className="bg-slate-50 rounded-lg p-3 text-left">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">Última avaliação</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreBadgeColor(completedAssessment.overall_score || 0)}`}>
                {Math.round(completedAssessment.overall_score || 0)}%
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-900">
                {completedAssessment.dominant_profile || 'Perfil não definido'}
              </p>
              <p className="text-xs text-slate-600">
                Concluída em {completedAssessment.completed_at ? formatDate(completedAssessment.completed_at) : 'Data não disponível'}
              </p>
            </div>
          </div>
        )}

        <div className="pt-2">
          <Link to="/results">
            <Button variant="outline" size="sm" className="w-full font-medium">
              Ver todos os resultados
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
};